import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface QuoteDetailProps {
  quoteId: number;
  onBack: () => void;
  onEdit: (quote: any) => void;
}

interface Devis {
  id: number;
  numero: string;
  client: {
    raison_sociale: string;
    email: string | null;
    code: string;
  };
  commercial: {
    name: string;
  } | null;
  date_devis: string;
  date_validite: string;
  notes_client: string | null;
  remise_globale: number;
  statut: string;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  lignes: Array<{
    designation: string;
    reference?: string;
    quantite: number;
    prix_unitaire_ht: number;
    remise_ligne: number;
    total_ht: number;
  }>;
}

export default function QuoteDetail({ quoteId, onBack, onEdit }: QuoteDetailProps) {
  const [quote, setQuote] = useState<Devis | null>(null);
  const [loading, setLoading] = useState(true);

  /* ================= LOAD DEVIS ================= */

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        setLoading(true);
        const res = await getResources(`devis-bcc/${quoteId}`);
        setQuote(res.data.data);
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Erreur chargement du devis');
        onBack();
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId, onBack]);

  if (loading || !quote) {
    return <div className="text-center py-12 text-gray-500">Chargement du devis…</div>;
  }

  /* ================= DONNÉES BACKEND (SOURCE UNIQUE) ================= */

  const totalHT = Number(quote.total_ht) || 0;
  const totalTVA = Number(quote.total_tva) || 0;
  const totalTTC = Number(quote.total_ttc) || 0;
  const remiseGlobale = Number(quote.remise_globale) || 0;

  const isExpired =
    new Date(quote.date_validite) < new Date() &&
    quote.statut === 'ENVOYE';

  /* ================= HELPERS ================= */

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'BROUILLON': return 'bg-gray-100 text-gray-800';
      case 'ENVOYE': return 'bg-blue-100 text-blue-800';
      case 'ACCEPTE': return 'bg-green-100 text-green-800';
      case 'REFUSE': return 'bg-red-100 text-red-800';
      case 'EXPIRE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'BROUILLON': return 'Brouillon';
      case 'ENVOYE': return 'Envoyé';
      case 'ACCEPTE': return 'Accepté';
      case 'REFUSE': return 'Refusé';
      case 'EXPIRE': return 'Expiré';
      default: return statut;
    }
  };

  /* ================= UI ================= */

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Retour à la liste
        </button>

        {quote.statut === 'BROUILLON' && (
          <button
            onClick={() => onEdit(quote)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <i className="ri-edit-line"></i>
            Modifier
          </button>
        )}
      </div>

      {/* INFOS GÉNÉRALES */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Informations générales</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <Info label="Numéro devis" value={quote.numero} />
          <Info
            label="Statut"
            value={
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(isExpired ? 'EXPIRE' : quote.statut)}`}>
                {getStatusLabel(isExpired ? 'EXPIRE' : quote.statut)}
              </span>
            }
          />

          <Info
            label="Client"
            value={`${quote.client.raison_sociale} (${quote.client.code})`}
          />

          <Info
            label="Email client"
            value={quote.client.email || 'Non renseigné'}
          />

          <Info
            label="Commercial"
            value={quote.commercial?.name || 'Non assigné'}
          />

          <Info
            label="Date devis"
            value={new Date(quote.date_devis).toLocaleDateString('fr-FR')}
          />

          <Info
            label="Valide jusqu’au"
            value={
              <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                {new Date(quote.date_validite).toLocaleDateString('fr-FR')}
              </span>
            }
          />

          <Info
            label="Remise globale"
            value={`${remiseGlobale.toFixed(2)} GNF`}
          />
        </div>

        {quote.notes_client && (
          <div className="mt-6">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <div className="border rounded-md bg-gray-50 p-3 whitespace-pre-wrap">
              {quote.notes_client}
            </div>
          </div>
        )}
      </div>

      {/* LIGNES */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Lignes du devis</h3>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Produit</th>
                <th className="text-right py-2">PU HT</th>
                <th className="text-center py-2">Qté</th>
                <th className="text-center py-2">Remise %</th>
                <th className="text-right py-2">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {quote.lignes.map((l, i) => (
                <tr key={i} className="border-b">
                  <td className="py-3">
                    <div className="font-medium">{l.designation}</div>
                    {l.reference && (
                      <div className="text-xs text-gray-500">{l.reference}</div>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    {Number(l.prix_unitaire_ht).toFixed(2)} GNF
                  </td>
                  <td className="py-3 text-center">{l.quantite}</td>
                  <td className="py-3 text-center">
                    {Number(l.remise_ligne).toFixed(2)} %
                  </td>
                  <td className="py-3 text-right font-medium">
                    {l.total_ht.toFixed(2)} GNF
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* TOTAUX */}
          <div className="mt-6 flex justify-end">
            <div className="w-80 space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total HT</span>
                <span>{totalHT.toFixed(2)} GNF</span>
              </div>

              {remiseGlobale > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Remise globale</span>
                  <span>-{remiseGlobale.toFixed(2)} GNF</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>TVA (19.25%)</span>
                <span>{totalTVA.toFixed(2)} GNF</span>
              </div>

              <div className="flex justify-between border-t pt-2 text-lg font-bold text-blue-600">
                <span>Total TTC</span>
                <span>{totalTTC.toFixed(2)} GNF</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= SMALL UI HELPER ================= */

function Info({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="border rounded-md bg-gray-50 px-3 py-2">
        {value}
      </div>
    </div>
  );
}
